import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output,
  QueryList, SimpleChanges, ViewChild, ViewChildren
} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {QuestionnaireEditorService} from '../../../services/questionnaire-editor.service';
import {flashFocus} from '../../../services/focus-flash';
import {QuestionnaireStateService} from '../../../services/questionnaire-state.service';
import {QuestionnaireVariablesService} from '../../../services/questionnaire-variables.service';
import {VariablesModalComponent} from '../variables/modal/variables-modal.component';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {FROALA_LICENSE_KEY} from '../../../licences/froala';
import {preventMultilineText} from '../../../froala/prevent-multiline-text';


// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionTypeMatrixComponent extends Translatable {}

@Component({
  selector: 'app-qa-question-matrix',
  templateUrl: './question-type-matrix.component.html',
  styleUrls: ['../../../shared/answer-row.css', './question-type-matrix.component.css', '../../../shared/reusable.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class QuestionTypeMatrixComponent implements OnInit, OnChanges, OnDestroy {
  @Input() question: Questionnaire.Container;
  @Input() previewFocusTarget: string = null;
  @Output() previewFocusHandled = new EventEmitter<void>();
  @ViewChildren('cellButton') cellButtons: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('cellDetail') cellDetail: ElementRef<HTMLElement>;

  private previewFocusTimer: ReturnType<typeof setTimeout> = null;
  private cellScrollTimer: ReturnType<typeof setTimeout> = null;

  public cols: Questionnaire.Container[] = [];
  public rows: Questionnaire.Container[] = [];
  public isScaleMode = false;
  public selectedRowId: string = null;
  public selectedColumnId: string = null;
  public hoveredRow = -1;
  public hoveredColumn = -1;

  public answerEditorOptions: any = {
    key: FROALA_LICENSE_KEY,
    events : {
      'paste.beforeCleanup': preventMultilineText,
      'contentChanged': () => this.updateElement()
    },
    pluginsEnabled: ['wordPaste', 'emoticons'],
    toolbarButtons: ['emoticons'],
    toolbarVisibleWithoutSelection: true,
    placeholderText: '...',
    emoticonsUseImage: false,
    quickInsertButtons: [],
    wordPasteModal: false,
    pastePlain: true,
    quickInsertTags: [],
    charCounterCount: false,
    toolbarInline: true,
    multiLine: false,
    language: 'de',
    width: '100%',
  };

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  public ngOnInit() {
    // init question elements if not set. this should never happen, but just in case...
    if (!this.question.elements) {
      this.question.elements = [];
    }

    for (const row of this.question.elements) {
      // cols must be the same on all rows (except for the pathHash and parentHash). we just pick the first one
      if (row.elements.length && !this.cols.length) {
        this.cols = this.clone(row.elements);
      }
    }

    // link rows
    this.rows = this.question.elements;

    if (this.question.extra && this.question.extra === 'scale') {
      this.rows.forEach(row => !row.appendix && (row.appendix = {'de_DE': ''}));
      this.isScaleMode = true;
    }
  }

  /** Opens and focuses the cell configuration requested by the central preview after rendering. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.previewFocusTarget) {
      return;
    }
    clearTimeout(this.previewFocusTimer);
    const target = this.previewFocusTarget || '';
    const match = /^cell-(\d+)-(\d+)$/.exec(target);
    if (!match) {
      return;
    }
    // a freshly selected question renders its options tab with the next frame, so the cell may not exist yet
    const focusCell = (attempt: number) => {
      const row = this.rows[Number(match[1])];
      const cell = row?.elements?.[Number(match[2])];
      const button = this.cellButtons?.find(item => item.nativeElement.getAttribute('data-cell-focus') === target)?.nativeElement;
      if (!button && attempt < 10) {
        this.previewFocusTimer = setTimeout(() => focusCell(attempt + 1), 50);
        return;
      }
      if (cell && button) {
        this.selectCell(row, cell);
        this.ref.detectChanges();
        button.focus({preventScroll: true});
        button.scrollIntoView({block: 'nearest', inline: 'nearest'});
        flashFocus(button, {inset: true});
        flashFocus(this.cellDetail?.nativeElement, {inset: true});
      }
      this.previewFocusHandled.emit();
    };
    this.previewFocusTimer = setTimeout(() => focusCell(0), 0);
  }

  /** Cancels pending focus when the properties panel is replaced. */
  public ngOnDestroy(): void {
    clearTimeout(this.previewFocusTimer);
    clearTimeout(this.cellScrollTimer);
  }

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private questionnaireEditorService: QuestionnaireEditorService,
    private questionnaireStateService: QuestionnaireStateService,
    private questionnaireVariables: QuestionnaireVariablesService,
    private ref: ChangeDetectorRef,
    private modal: NgbModal) {
  }

  /**
   * Opens modal for questionnaire variables for given container
   *
   * @param {Questionnaire.Container} container
   * @param {boolean} disableInline
   */
  public onVariablesModal(container: Questionnaire.Container, opts = {inline: true, upload: true}) {
    const modalRef = this.modal.open(VariablesModalComponent, {size: 'lg'});
    modalRef.componentInstance.disabledScopes = opts || {inline: true, upload: true, 'pdf-form': true};
    modalRef.componentInstance.container = container;
  }

  /** Returns the selected row by identity, including after reordering. */
  public get selectedRow(): Questionnaire.Container {
    return this.rows.find(row => row.id === this.selectedRowId) || null;
  }

  /** Returns the selected column definition. */
  public get selectedColumn(): Questionnaire.Container {
    return this.cols.find(col => col.id === this.selectedColumnId) || null;
  }

  /** Recreates the cell editor when its actual container changes. */
  public get selectedCells(): Questionnaire.Container[] {
    const row = this.selectedRow;
    const cell = row && row.elements.find(col => col.id === this.selectedColumnId);
    return cell ? [cell] : [];
  }

  /** Selects a cell and reveals its settings after the detail panel has rendered. */
  public selectCell(row: Questionnaire.Container, column: Questionnaire.Container) {
    this.selectedRowId = row.id;
    this.selectedColumnId = column.id;
    clearTimeout(this.cellScrollTimer);
    this.cellScrollTimer = setTimeout(() => {
      this.cellDetail?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'});
    }, 0);
  }

  /** Preserves matrix buttons when a text-field blur rebuilds the column objects. */
  public trackMatrixElement(index: number, element: Questionnaire.Container): string {
    return element.id;
  }

  /** Keeps variable labels mounted when hover or focus refreshes the cell bindings. */
  public trackCellVariable(index: number, variable: {scope: string}): string {
    return variable.scope;
  }

  /** Returns all variable mappings for a cell except its separate scoring mapping. */
  public getCellVariables(container: Questionnaire.Container): {scope: string, name: string, label: string}[] {
    if (!container.pathHash) {
      return [];
    }
    const variables = this.questionnaireStateService.getQuestionnaire().meta.options.variables || {};
    const scopes = this.questionnaireVariables.getVariableScopes();
    return Object.keys(variables).filter(scope => scope !== 'scoring').map(scope => ({
      scope,
      name: this.questionnaireVariables.getMappedVariableName(scope, container.pathHash),
      label: scopes[scope] ? scopes[scope].name : scope
    })).filter(variable => !!variable.name);
  }

  /**
   * Gets scoring information for a matrix cell container
   */
  public getScoringInfo(container: Questionnaire.Container): {variableName: string, value: number} | null {
    if (!container || !container.pathHash) {
      return null;
    }
    const scope = 'scoring';
    const variableName = this.questionnaireVariables.getMappedVariableName(scope, container.pathHash);
    if (!variableName) {
      return null;
    }
    const opts = this.questionnaireVariables.getMappedVariableOptions(scope, container.pathHash, variableName);
    const value = (opts && typeof opts.value === 'number') ? opts.value : 0;
    return { variableName, value };
  }

  /**
   * Toggles the mandatory flag on a matrix row
   */
  public onToggleRowMandatory(row: any) {
    !row.options && (row.options = {});
    row.options.mandatory = !row.options.mandatory;
    this.updateElement();
  }

  /**
   * Adds a row to this question
   */
  public onAddRow() {
    // generate id for new answer element
    const id = this.questionnaireEditorService.generateUUID();

    // build new row container. link cols to elements
    const row = {
      id,
      path: null,
      pathHash: null,
      parentHash: null,
      title: {'de_DE': ''},
      appendix: {'de_DE': ''},
      elements: this.cols
    };

    // push row container to question
    this.rows.push(row);
    this.updateElement();
  }

  /**
   * Deletes a row from this question
   *
   * @param {number} position
   */
  public onDeleteRow(position: number) {
    this.questionnaireEditorService.confirmDelete(() => {
      const row = this.rows[position];
      row && row.pathHash && this.questionnaireVariables.deleteVariableMappings(row.pathHash);
      this.rows.splice(position, 1);

      // update element
      this.updateElement();
    });
  }

  /**
   * Adds a column to this question
   */
  public onAddColumn() {
    // generate id for new answer element
    const id = this.questionnaireEditorService.generateUUID();

    // build new answer
    const column = {
      id,
      path: null,
      pathHash: null,
      parentHash: null,
      title: { 'de_DE': ''}
    };

    // push column element to question
    this.cols.push(column);
    this.updateElement();
  }

  /**
   * Deletes a column from this question
   *
   * @param {number} position
   */
  public onDeleteColumn(position: number) {
    this.questionnaireEditorService.confirmDelete(() => {
      this.cols.splice(position, 1);
      this.updateElement();
    });
  }

  /**
   * Changes the position of a row from current to new
   *
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  public onRowPositionChange(currentPosition: number, newPosition: number) {
    // change position of element in rows
    this.changePosition(this.rows, currentPosition, newPosition);
  }

  /**
   * Changes the position of a column from current to new
   *
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  public onColPositionChange(currentPosition: number, newPosition: number) {
    // change position of element in rows
    this.changePosition(this.cols, currentPosition, newPosition);
  }

  /**
   * Rebuilds json and updates element in state service
   */
  public updateElement() {
    // update columns in all rows
    for (const row of this.question.elements) {
      const cols = this.clone(this.cols);
      row.elements = cols;
    }

    // update element (updates path hashes, triggers events)
    this.questionnaireStateService.updateElement(this.question, -1);

    this.ref.markForCheck();
    this.ref.detectChanges();
  }

  /**
   * Changes the position of an element in given elements array by current and new
   *
   * @param {any[]} elements
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  private changePosition(elements: any[], currentPosition: number, newPosition: number) {
    // early bailout when changing positions does not make sense
    if (currentPosition >= (elements.length) || newPosition < 0) {
      return;
    }

    // reorder answers by current and new position
    elements.splice(newPosition, 0, elements.splice(currentPosition, 1)[0]);
    this.updateElement();
  }

  /**
   * Clones the given object
   *
   * @param {any} obj
   * @returns {any}
   */
  private clone(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }
}
